/**
 * Built-in helper functions for the v2 template engine.
 * All helpers accept a string cell value and return a string.
 * null/undefined input is treated as "".
 *
 * See docs/V2-PLAN.md §2 "Built-in computed fns".
 */

import type { HelperRegistry } from "./types";

function toStr(s: unknown): string {
  if (s == null) return "";
  return String(s);
}

/**
 * Filler words the "smart" slug style drops: articles, conjunctions,
 * possessive / demonstrative determiners and bare forms of "to be".
 * Prepositions ("in", "with", "for") are kept — they carry meaning in
 * product names ("kurta with dupatta").
 */
const SLUG_STOP_WORDS = new Set([
  "a", "an", "the",
  "and", "or", "nor", "but",
  "my", "your", "our", "their", "his", "her", "its",
  "this", "that", "these", "those",
  "is", "are", "was", "were", "be",
]);

/** Default length cap (characters) for smart slugs; cut at a word boundary. */
export const SMART_SLUG_MAX_LENGTH = 70;

/** "plain" keeps every word (the original behaviour); anything else is smart. */
export type SlugStyle = "smart" | "plain";

/** The original slug: lowercase, non-alphanumeric runs → "-". Kept verbatim so
 *  templates that pick the "plain" style produce byte-identical handles. */
function plainSlug(s: string): string {
  return toStr(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Join words with "-" without exceeding `max` chars (0 = no cap). Never cuts a word
 *  in half, but always keeps at least the first word. */
function joinWithin(words: string[], max: number): string {
  if (max <= 0) return words.join("-");
  let out = "";
  for (const w of words) {
    const next = out.length === 0 ? w : `${out}-${w}`;
    if (next.length > max && out.length > 0) break;
    out = next;
  }
  return out.length > max ? out.slice(0, max).replace(/-+$/, "") : out;
}

/**
 * URL slug for a Shopify Handle.
 *
 * Smart (default): accents folded (é → e), apostrophes dropped (men's → mens),
 * HTML entities removed, filler words dropped and repeated words removed, then
 * capped at SMART_SLUG_MAX_LENGTH characters on a word boundary.
 *   "A cat in my house"        → "cat-in-house"
 *   "The Rani Pink Silk Saree" → "rani-pink-silk-saree"
 *
 * `style: "plain"` keeps every word: "A cat in my house" → "a-cat-in-my-house".
 * `maxLength` overrides the cap ("0" = none). Called as a computed fn
 * (`slugify(text, style, maxLength)`) or a pipe (`| slugify:"plain"`).
 */
export function slugify(s: string, style?: string, maxLength?: string): string {
  const parsedMax = Number.parseInt(String(maxLength ?? "").trim(), 10);
  const hasMax = Number.isFinite(parsedMax) && parsedMax >= 0;

  if (String(style ?? "").trim().toLowerCase() === "plain") {
    const plain = plainSlug(s);
    return hasMax && parsedMax > 0
      ? joinWithin(plain.split("-").filter(Boolean), parsedMax)
      : plain;
  }

  const words = toStr(s)
    .replace(/&(amp|nbsp|quot|apos|#39|lt|gt);/gi, " ")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['\u2019`]/g, "")
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 0);

  const kept: string[] = [];
  const seen = new Set<string>();
  for (const w of words) {
    if (SLUG_STOP_WORDS.has(w)) continue;
    // Drop a repeated word ("Silk Saree - Silk"), but never a number: "2 pack 2" is meaningful.
    if (!/^\d+$/.test(w)) {
      if (seen.has(w)) continue;
      seen.add(w);
    }
    kept.push(w);
  }
  // A title made only of filler words ("The One") keeps its words.
  const base = kept.length > 0 ? kept : words;
  return joinWithin(base, hasMax ? parsedMax : SMART_SLUG_MAX_LENGTH);
}

/** Uppercase the entire string. */
function upper(s: string): string {
  return toStr(s).toUpperCase();
}

/** Lowercase the entire string. */
function lower(s: string): string {
  return toStr(s).toLowerCase();
}

/**
 * Title-case each space-separated word.
 * e.g. "hello world" → "Hello World"
 */
function title(s: string): string {
  return toStr(s)
    .split(" ")
    .map((word) =>
      word.length > 0 ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word
    )
    .join(" ");
}

/** Trim leading and trailing whitespace. */
function trim(s: string): string {
  return toStr(s).trim();
}

/**
 * Keep only digits and at most one decimal point.
 * e.g. "5.5 metres" → "5.5", "₹13,599" → "13599", "3.1.4" → "3.14"
 */
function numberOnly(s: string): string {
  const str = toStr(s);
  let result = "";
  let dotSeen = false;
  for (const ch of str) {
    if (ch >= "0" && ch <= "9") {
      result += ch;
    } else if (ch === "." && !dotSeen) {
      dotSeen = true;
      result += ch;
    }
  }
  return result;
}

/**
 * If `s` is empty or whitespace-only, return `fallback`; else return `s`.
 * Called as `{{Header | default:"--"}}` → invoked as default(value, '--').
 */
function defaultHelper(s: string, fallback = ""): string {
  const str = toStr(s);
  return str.trim().length === 0 ? fallback : str;
}

export const defaultHelpers: HelperRegistry = {
  slugify,
  upper,
  lower,
  title,
  trim,
  numberOnly,
  default: defaultHelper,
};
